<template>
  <div class="subject-fee-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Subject Wise Fees</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalStudents }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading subject fee records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <div class="report-header-modern">
        <div class="d-flex align-items-start">
          <div class="logo-wrapper mr-2" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img">
          </div>
          <div class="header-text-block flex-grow-1">
            <h2 class="school-name-full mb-0">
              {{ campusName }}<span v-if="campusBranch" class="text-muted"> - {{ campusBranch }}</span>
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Subject Wise Fees Report</span>
              <span class="date-range-text">Generated on {{ currentDateTime }}</span>
            </div>
            <div class="class-section-banner mt-2">
              <span><strong>Class:</strong> {{ classLabel || '-' }}</span>
              <span class="banner-sep">|</span>
              <span><strong>Section:</strong> {{ sectionLabel || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center col-index">#</th>
              <th class="text-center col-gr">GR NO</th>
              <th class="text-left col-name">STUDENT NAME</th>
              <th
                v-for="subject in subjectColumns"
                :key="subject.id"
                class="text-center subject-col"
              >
                {{ subject.name }}
              </th>
              <th class="text-right total-col">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(student, index) in reportStudents"
              :key="student.sID || index"
            >
              <td class="text-center col-index">{{ index + 1 }}</td>
              <td class="text-center col-gr">{{ student.grNo || '-' }}</td>
              <td class="text-left col-name">{{ (student.studentName || '-').toUpperCase() }}</td>
              <td
                v-for="subject in subjectColumns"
                :key="`${student.sID}-${subject.id}`"
                class="text-center subject-fee-cell"
                :class="getCellClass(student, subject.id)"
              >
                <span v-if="!isStudentEnrolled(student, subject.id)" class="not-enrolled-mark">✕</span>
                <span v-else-if="isSubjectExcluded(student, subject.id)" class="excluded-mark">—</span>
                <span v-else>{{ formatCurrency(getStudentSubjectFee(student, subject.id)) }}</span>
              </td>
              <td class="text-right total-col font-weight-bold">
                {{ formatCurrency(student.totalSubjectFee) }}
              </td>
            </tr>
            <tr v-if="reportStudents.length === 0">
              <td :colspan="columnCount" class="text-center py-4 no-data">
                No subject fee records found for the selected criteria
              </td>
            </tr>
          </tbody>
          <tfoot v-if="reportStudents.length > 0">
            <tr class="grand-total-row">
              <td colspan="3" class="text-right font-weight-bold">Grand Total</td>
              <td
                v-for="subject in subjectColumns"
                :key="`total-${subject.id}`"
                class="text-right font-weight-bold"
              >
                {{ formatCurrency(getSubjectColumnTotal(subject.id)) }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatCurrency(grandTotalSubjectFee) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'SubjectFeeReportPrint',
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportStudents: [],
      totalStudents: 0,
      campusName: 'MY SKOOL',
      campusBranch: '',
      schoolLogo: null,
      db: '',
      cID: '',
      classID: 0,
      sectionID: 0,
      sessionID: 0,
      classLabel: '',
      sectionLabel: '',
    }
  },
  computed: {
    currentDateTime() {
      const now = new Date()
      return now.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    subjectColumns() {
      const map = new Map()
      this.reportStudents.forEach((student) => {
        ;(student.subjects || []).forEach((subject) => {
          const id = subject.subjectID
          if (id != null && !map.has(id)) {
            map.set(id, subject.subjectName || `Subject ${id}`)
          }
        })
      })
      return Array.from(map.entries())
        .map(([id, name]) => ({ id: Number(id), name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    columnCount() {
      return 3 + this.subjectColumns.length + 1
    },
    grandTotalSubjectFee() {
      return this.reportStudents.reduce(
        (sum, student) => sum + (Number(student.totalSubjectFee) || 0),
        0
      )
    },
  },
  created() {
    this.initReport()
  },
  methods: {
    initReport() {
      const query = this.$route.query
      this.db = query.db || this.$store.state.userData.db
      this.cID = query.cID || 0
      this.classID = query.classID || 0
      this.sectionID = query.sectionID || 0
      this.sessionID = parseInt(query.sessionID) || 0
      this.classLabel = query.classLabel || ''
      this.sectionLabel = query.sectionLabel || ''

      if (!this.db) {
        this.error = 'Database identifier is missing. Please close and try again.'
        return
      }

      this.loadCampusInfo()
      this.loadReportData()
    },
    async loadCampusInfo() {
      try {
        const url = 'https://apiv26.myskool.app/api/campuses'
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: 'application/json',
          },
        })

        if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
          const match = response.data.data.find((c) => c.id == this.cID)
          if (match) {
            this.campusName = match.name
            this.campusBranch = this.cID == 0 ? 'All Campuses' : match.branch
            this.schoolLogo = match.logo
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || []
        const currentBranch = branches.find((b) => b.id == this.cID) || this.$store.state.currentBranch
        if (currentBranch) {
          this.campusName = currentBranch.organizationName || currentBranch.branch || 'MY SKOOL'
          this.campusBranch = currentBranch.branch || ''
        }
      }
    },
    async loadReportData() {
      this.loading = true
      this.error = null

      try {
        const url = `${this.$store.state.domain}StudentSubjectsEnrollnment/GetSubjectFeeReport`
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            classID: this.classID || 0,
            sectionID: this.sectionID || 0,
            sessionID: this.sessionID || 0,
            sID: 0,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: 'application/json',
          },
        })

        if (response.data && response.data.status === 'success') {
          this.reportStudents = response.data.data || []
          this.totalStudents = response.data.totalStudents || this.reportStudents.length

          if (!this.classLabel && this.reportStudents.length) {
            this.classLabel = this.reportStudents[0].className || ''
            this.sectionLabel = this.reportStudents[0].sectionName || ''
          }
        } else {
          this.error = response.data?.message || 'The server returned an unexpected response format.'
        }
      } catch (err) {
        console.error('Error fetching subject fee report:', err)
        this.error = 'Failed to retrieve report data. Please ensure the API endpoint is accessible.'
      } finally {
        this.loading = false
      }
    },
    findStudentSubject(student, subjectID) {
      return (student.subjects || []).find((s) => Number(s.subjectID) === Number(subjectID))
    },
    isStudentEnrolled(student, subjectID) {
      return !!this.findStudentSubject(student, subjectID)
    },
    isSubjectExcluded(student, subjectID) {
      const subject = this.findStudentSubject(student, subjectID)
      return subject ? subject.isExcluded : false
    },
    getStudentSubjectFee(student, subjectID) {
      const subject = this.findStudentSubject(student, subjectID)
      return subject ? subject.subjectFee : null
    },
    getCellClass(student, subjectID) {
      if (!this.isStudentEnrolled(student, subjectID)) return 'not-enrolled-cell'
      if (this.isSubjectExcluded(student, subjectID)) return 'excluded-cell'
      return 'enrolled-cell'
    },
    getSubjectColumnTotal(subjectID) {
      return this.reportStudents.reduce((sum, student) => {
        const subject = this.findStudentSubject(student, subjectID)
        if (!subject || subject.isExcluded) return sum
        return sum + (Number(subject.subjectFee) || 0)
      }, 0)
    },
    formatCurrency(value) {
      if (value === undefined || value === null || value === '') return '0'
      return new Intl.NumberFormat('en-PK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    },
    print() {
      window.print()
    },
    close() {
      window.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.subject-fee-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 1rem;
  font-family: 'Outfit', 'Inter', sans-serif;
  color: #2d3436;
  font-size: 12px;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0.5rem;
  z-index: 1000;

  h4 {
    font-weight: 700;
    font-size: 1rem;
    margin: 0;
  }
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  border: none;
  outline: none;
}

.btn-print {
  background: #6c5ce7;
  color: white;
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.report-page {
  background: #fff;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 10px;
  margin-bottom: 10px;

  .school-logo-img {
    height: 70px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.25rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    line-height: 1.2;
  }

  .report-info-line {
    font-size: 0.85rem;
    color: #444;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;

    .report-name-text {
      font-weight: 700;
    }

    .date-range-text {
      color: #666;
    }
  }

  .class-section-banner {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    background: #f1f3f5;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 0.9rem;
    font-weight: 600;

    .banner-sep {
      color: #adb5bd;
    }
  }
}

.table-container {
  width: 100%;
  overflow-x: auto;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #ccc;
  table-layout: fixed;
  font-size: 11px;

  th,
  td {
    border: 1px solid #ccc;
    padding: 4px 5px;
    line-height: 1.25;
    vertical-align: middle;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  thead th {
    background: #2c3e50;
    color: #fff;
    font-weight: 700;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    padding: 5px 4px;
  }

  .col-index {
    width: 28px;
    min-width: 28px;
  }

  .col-gr {
    width: 72px;
    min-width: 72px;
  }

  .col-name {
    width: 140px;
    min-width: 120px;
    text-align: left !important;
    font-weight: 600;
  }

  .subject-col {
    width: 62px;
    min-width: 58px;
    font-size: 8px;
    line-height: 1.15;
    white-space: normal;
    hyphens: auto;
  }

  .total-col {
    width: 68px;
    min-width: 64px;
  }

  tbody tr:nth-child(even) {
    background: #fafafa;
  }

  tfoot .grand-total-row {
    background: #eef2ff;
    font-size: 10px;
  }

  .subject-fee-cell {
    font-size: 10px;
  }

  .not-enrolled-cell {
    background: #e9ecef !important;
    color: #868e96;
    font-size: 9px;
  }

  .not-enrolled-mark {
    display: inline-block;
    font-size: 9px;
    color: #adb5bd;
    font-weight: 700;
  }

  .excluded-cell {
    background: #fff3cd !important;
    color: #856404;
  }

  .excluded-mark {
    font-size: 9px;
    color: #b8860b;
  }

  .enrolled-cell {
    background: #fff;
  }

  .no-data {
    font-size: 0.9rem;
    color: #666;
  }
}

@page {
  size: A4 landscape;
  margin: 8mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  .subject-fee-report {
    background: #fff !important;
    padding: 0 !important;
    font-size: 9pt;
  }

  .report-page {
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
  }

  .report-header-modern {
    margin-bottom: 6px !important;
    padding-bottom: 6px !important;

    .school-logo-img {
      height: 55px !important;
    }

    .school-name-full {
      font-size: 13pt !important;
    }

    .report-info-line,
    .class-section-banner {
      font-size: 8pt !important;
    }

    .class-section-banner {
      padding: 2px 6px !important;
    }
  }

  .table-container {
    overflow: visible !important;
  }

  .ledger-style-table {
    width: 100% !important;
    font-size: 7pt !important;
    page-break-inside: auto;

    th,
    td {
      padding: 2px 3px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    .not-enrolled-cell {
      background: #d0d3d8 !important;
      color: #555 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .not-enrolled-mark {
      color: #666 !important;
      font-size: 7pt !important;
    }

    .excluded-cell {
      background: #f5f0d0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    thead {
      display: table-header-group;
    }

    thead th {
      background: #eee !important;
      color: #000 !important;
      font-size: 6pt !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr {
      page-break-inside: avoid;
    }

    tfoot {
      display: table-footer-group;
    }

    .col-index {
      width: 22px !important;
    }

    .col-gr {
      width: 58px !important;
    }

    .col-name {
      width: 110px !important;
    }

    .subject-col {
      width: auto !important;
      font-size: 5.5pt !important;
    }

    .total-col {
      width: 52px !important;
    }
  }
}
</style>
